/**
 * FullscreenManager handles fullscreen mode for panels
 */
export class FullscreenManager {
  constructor() {
    this.currentFullscreenPanel = null;
    this.originalParent = null;
    this.originalNextSibling = null;
    this.overlay = null;
    this.escapeHandler = null;
    
    this.init();
  }

  /**
   * Initialize the fullscreen manager
   */
  init() {
    // Create overlay element
    this.overlay = document.createElement('div');
    this.overlay.className = 'fullscreen-overlay';
    document.body.appendChild(this.overlay);

    // Set up escape key handler
    this.escapeHandler = (event) => {
      if (event.key === 'Escape' && this.currentFullscreenPanel) {
        this.exitFullscreen();
      }
    };
  }

  /**
   * Enter fullscreen mode for a panel
   * @param {HTMLElement} panel - The panel element to make fullscreen
   * @param {string} panelType - Type of panel ('input' or 'output')
   */
  enterFullscreen(panel, panelType) {
    if (this.currentFullscreenPanel) {
      this.exitFullscreen();
    }

    // Store original position
    this.originalParent = panel.parentNode;
    this.originalNextSibling = panel.nextSibling;
    this.currentFullscreenPanel = panel;

    // Add fullscreen class and move to body
    panel.classList.add('fullscreen-mode');
    document.body.appendChild(panel);

    // Show overlay
    this.overlay.classList.add('active');

    // Add escape key listener
    document.addEventListener('keydown', this.escapeHandler);

    // Update fullscreen button state
    this.updateButtonStates(panelType, true);

    // Emit custom event
    document.dispatchEvent(new CustomEvent('fullscreenEnter', {
      detail: { panel, panelType }
    }));

    // Prevent body scroll
    document.body.style.overflow = 'hidden';
  }

  /**
   * Exit fullscreen mode
   */
  exitFullscreen() {
    if (!this.currentFullscreenPanel) return;

    const panel = this.currentFullscreenPanel;
    const panelType = panel.classList.contains('input-panel') ? 'input' : 'output';

    // Remove fullscreen class
    panel.classList.remove('fullscreen-mode');

    // Restore original position
    if (this.originalNextSibling) {
      this.originalParent.insertBefore(panel, this.originalNextSibling);
    } else {
      this.originalParent.appendChild(panel);
    }

    // Hide overlay
    this.overlay.classList.remove('active');

    // Remove escape key listener
    document.removeEventListener('keydown', this.escapeHandler);

    // Update fullscreen button state
    this.updateButtonStates(panelType, false);

    // Emit custom event
    document.dispatchEvent(new CustomEvent('fullscreenExit', {
      detail: { panel, panelType }
    }));

    // Restore body scroll
    document.body.style.overflow = '';

    // Clear references
    this.currentFullscreenPanel = null;
    this.originalParent = null;
    this.originalNextSibling = null;
  }

  /**
   * Toggle fullscreen mode for a panel
   * @param {HTMLElement} panel - The panel element
   * @param {string} panelType - Type of panel ('input' or 'output')
   */
  toggleFullscreen(panel, panelType) {
    if (this.isFullscreen(panel)) {
      this.exitFullscreen();
    } else {
      this.enterFullscreen(panel, panelType);
    }
  }

  /**
   * Check if a panel is currently in fullscreen mode
   * @param {HTMLElement} panel - The panel element to check
   * @returns {boolean}
   */
  isFullscreen(panel) {
    return this.currentFullscreenPanel === panel;
  }

  /**
   * Get the currently fullscreen panel
   * @returns {HTMLElement|null}
   */
  getCurrentFullscreenPanel() {
    return this.currentFullscreenPanel;
  }

  /**
   * Update fullscreen button states
   * @param {string} activePanel - The panel type that's active ('input' or 'output')
   * @param {boolean} isFullscreen - Whether fullscreen is active
   */
  updateButtonStates(activePanel, isFullscreen) {
    const inputBtn = document.querySelector('.input-fullscreen-btn');
    const outputBtn = document.querySelector('.output-fullscreen-btn');

    if (inputBtn) {
      inputBtn.classList.toggle('active', activePanel === 'input' && isFullscreen);
      inputBtn.title = activePanel === 'input' && isFullscreen ? 'Exit fullscreen (Esc)' : 'Enter fullscreen';
      
      const icon = inputBtn.querySelector('.icon');
      if (icon) {
        icon.textContent = activePanel === 'input' && isFullscreen ? '⤓' : '⤢';
      }
    }

    if (outputBtn) {
      outputBtn.classList.toggle('active', activePanel === 'output' && isFullscreen);
      outputBtn.title = activePanel === 'output' && isFullscreen ? 'Exit fullscreen (Esc)' : 'Enter fullscreen';
      
      const icon = outputBtn.querySelector('.icon');
      if (icon) {
        icon.textContent = activePanel === 'output' && isFullscreen ? '⤓' : '⤢';
      }
    }
  }

  /**
   * Add fullscreen buttons to panels
   */
  addFullscreenButtons() {
    // Add button to input panel toolbar
    const inputToolbar = document.querySelector('.input-toolbar');
    if (inputToolbar && !inputToolbar.querySelector('.input-fullscreen-btn')) {
      const inputBtn = this.createFullscreenButton('input', 'Fullscreen JSON Input');
      inputToolbar.appendChild(inputBtn);
    }

    // Add button to output panel toolbar
    const outputToolbar = document.querySelector('.output-toolbar');
    if (outputToolbar && !outputToolbar.querySelector('.output-fullscreen-btn')) {
      const outputBtn = this.createFullscreenButton('output', 'Fullscreen Tree View');
      outputToolbar.appendChild(outputBtn);
    }
  }

  /**
   * Create a fullscreen button
   * @param {string} panelType - Type of panel ('input' or 'output')
   * @param {string} title - Button title
   * @returns {HTMLElement}
   */
  createFullscreenButton(panelType, title) {
    const button = document.createElement('button');
    button.className = `fullscreen-btn ${panelType}-fullscreen-btn`;
    button.title = title;
    button.innerHTML = `
      <span class="icon">⤢</span>
      <span class="text">Fullscreen</span>
    `;

    button.addEventListener('click', () => {
      const panel = document.querySelector(`.${panelType}-panel`);
      if (panel) {
        this.toggleFullscreen(panel, panelType);
      }
    });

    return button;
  }

  /**
   * Cleanup resources
   */
  destroy() {
    if (this.currentFullscreenPanel) {
      this.exitFullscreen();
    }

    if (this.overlay && this.overlay.parentNode) {
      this.overlay.parentNode.removeChild(this.overlay);
    }

    document.removeEventListener('keydown', this.escapeHandler);
  }
}